/**
 * Anthropic Client Service (Backend Proxy)
 * 
 * All Anthropic API calls are routed through the backend server,
 * keeping the API key server-side and out of the browser bundle.
 */

const API_BASE = import.meta.env.VITE_API_URL || 'https://app.pvslimited.co.uk';

let clientAvailable = null;

/**
 * Check if the backend Anthropic proxy is available
 * @returns {boolean}
 */
export const isClientInitialized = () => {
  if (clientAvailable !== null) return clientAvailable;

  fetch(`${API_BASE}/api/vesselmind/chat/status`)
    .then(res => res.json())
    .then(data => { clientAvailable = data.available; })
    .catch(() => { clientAvailable = false; });

  return true;
};

/**
 * Get API key status (the key is on the server now)
 * @returns {object}
 */
export const getApiKeyStatus = () => {
  return {
    isSet: clientAvailable !== false,
    length: 0,
    preview: 'server-managed'
  };
};

/**
 * Initialize client — now a no-op since key is server-side.
 * Kept for backwards compatibility with ApiKeySetup component.
 * @param {string} key - ignored
 */
export const initializeClient = (key) => {
  console.log('API key is now managed server-side');
  clientAvailable = true;
};

/**
 * Clear the API key — no-op since key is server-side
 */
export const clearApiKey = () => {
  console.log('API key is managed server-side');
};

/**
 * Send a chat completion request via the backend proxy (non-streaming)
 * @param {Array} messages - Array of message objects {role, content}
 * @param {string} context - PDF context for the query
 * @param {object} options - Additional options
 * @returns {Promise<object>} Response from Claude
 */
export const sendChatCompletion = async (messages, context, options = {}) => {
  try {
    const response = await fetch(`${API_BASE}/api/vesselmind/chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ messages, context, options })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        error: errorData.error || 'Request failed',
        errorType: errorData.errorType || 'unknown'
      };
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let fullText = '';
    let usage = { inputTokens: 0, outputTokens: 0, cacheCreationTokens: 0, cacheReadTokens: 0 };

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      const chunk = decoder.decode(value, { stream: true });
      const lines = chunk.split('\n');

      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        if (line.startsWith('data: ')) {
          const eventLine = i > 0 ? lines[i - 1] : '';
          const data = JSON.parse(line.slice(6));

          if (eventLine === 'event: text') {
            fullText += data.text;
          } else if (eventLine === 'event: usage_start') {
            usage.inputTokens = data.inputTokens || 0;
            usage.cacheCreationTokens = data.cacheCreationTokens || 0;
            usage.cacheReadTokens = data.cacheReadTokens || 0;
          } else if (eventLine === 'event: usage_final') {
            usage.outputTokens = data.outputTokens || 0;
          }
        }
      }
    }

    return {
      success: true,
      content: fullText,
      usage,
      model: options.model || 'claude-sonnet-4-20250514',
      stopReason: 'end_turn'
    };

  } catch (error) {
    console.error('Chat proxy error:', error);
    return {
      success: false,
      error: error.message || 'An error occurred while processing your request.',
      errorType: 'unknown'
    };
  }
};

/**
 * Send a streaming chat completion request via the backend proxy
 * @param {Array} messages - Array of message objects {role, content}
 * @param {string} context - PDF context for the query
 * @param {Function} onChunk - Callback for each text chunk (chunk) => void
 * @param {object} options - Additional options
 * @returns {Promise<object>} Final response metadata
 */
export const sendChatCompletionStream = async (messages, context, onChunk, options = {}) => {
  try {
    const response = await fetch(`${API_BASE}/api/vesselmind/chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ messages, context, options })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        error: errorData.error || 'Request failed',
        errorType: errorData.errorType || 'unknown'
      };
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let fullText = '';
    let cacheStats = {
      inputTokens: 0,
      cacheCreationTokens: 0,
      cacheReadTokens: 0,
      outputTokens: 0
    };
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const events = buffer.split('\n\n');
      buffer = events.pop() || '';

      for (const event of events) {
        const lines = event.split('\n');
        let eventType = '';
        let eventData = '';

        for (const line of lines) {
          if (line.startsWith('event: ')) eventType = line.slice(7);
          else if (line.startsWith('data: ')) eventData = line.slice(6);
        }

        if (!eventData) continue;

        try {
          const data = JSON.parse(eventData);

          if (eventType === 'text') {
            fullText += data.text;
            onChunk(data.text);
          } else if (eventType === 'usage_start') {
            cacheStats.inputTokens = data.inputTokens || 0;
            cacheStats.cacheCreationTokens = data.cacheCreationTokens || 0;
            cacheStats.cacheReadTokens = data.cacheReadTokens || 0;
          } else if (eventType === 'usage_final') {
            cacheStats.outputTokens = data.outputTokens || 0;
          } else if (eventType === 'error') {
            return {
              success: false,
              error: data.error || 'Streaming error',
              errorType: data.errorType || 'unknown'
            };
          }
        } catch (parseErr) {
          // skip malformed events
        }
      }
    }

    if (cacheStats.cacheCreationTokens > 0) {
      console.log(`Cache WRITE: ${cacheStats.cacheCreationTokens} tokens cached`);
    }
    if (cacheStats.cacheReadTokens > 0) {
      console.log(`Cache HIT: ${cacheStats.cacheReadTokens} tokens from cache (saved ~90% cost!)`);
    }

    return {
      success: true,
      content: fullText,
      usage: {
        inputTokens: cacheStats.inputTokens,
        outputTokens: cacheStats.outputTokens,
        cacheCreationTokens: cacheStats.cacheCreationTokens,
        cacheReadTokens: cacheStats.cacheReadTokens
      },
      model: options.model || 'claude-sonnet-4-20250514',
      stopReason: 'end_turn'
    };

  } catch (error) {
    console.error('Streaming proxy error:', error);
    return {
      success: false,
      error: error.message || 'An unexpected error occurred. Please try again.',
      errorType: 'unknown'
    };
  }
};

// Backwards-compatible alias
export const sendStreamingChatCompletion = sendChatCompletionStream;

/**
 * Validate context quality
 * @param {string} context - Context to validate
 * @returns {object} Validation result
 */
export const validateContext = (context) => {
  if (!context || context.trim().length === 0) {
    return {
      valid: false,
      reason: 'No context provided',
      confidence: 0
    };
  }

  const wordCount = context.split(/\s+/).length;

  if (wordCount < 50) {
    return {
      valid: false,
      reason: 'Context too short (minimum 50 words)',
      confidence: 0.2,
      wordCount
    };
  }

  return {
    valid: true,
    confidence: Math.min(wordCount / 500, 1.0),
    wordCount
  };
};
